import React, { useEffect, useState, useRef } from "react";
import Box from "@mui/joy/Box";
import { Typography } from "@mui/joy";
import useIsInappWebview from "hooks/useIsInappWebview";
import { theme } from "styles/theme";
import { XMarkIconSvg } from "static/image/common/SvgAssets";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { axiosInstance } from "api";
import {
  HOME_DOMAIN,
  HOME_NOTICE_BANNER_DETAIL_ENDPOINT,
  homeApi,
} from "api/homeApi";
import { useRecoilValue } from "recoil";
import { userAtom } from "recoil/userAtom";
import { taskListAtom } from "recoil/taskListAtom";
import { getDoneModalContent } from "utils/modalUtil";
import { homeThoughtRecordListAtom } from "recoil/homeThoughtRecordListAtom";

export default function NoticeBanner() {
  const isInappWebview = useIsInappWebview();
  const userState = useRecoilValue(userAtom);
  const queryClient = useQueryClient();
  const taskListState = useRecoilValue(taskListAtom);
  const homeThoughtRecordList = useRecoilValue(homeThoughtRecordListAtom);
  const [showNoticeBanner, setShowNoticeBanner] = useState<boolean>(false);
  const [bannerMessage, setBannerMessage] = useState<string | undefined>(
    undefined
  );

  // Track previous message to animate only when it actually changes
  const prevMessageRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    prevMessageRef.current = bannerMessage;
  }, [bannerMessage]);

  const noticeBannerQuery = useQuery({
    enabled:
      !!axiosInstance.defaults.headers["X-Auth-Token"] && !!userState?.userId,
    queryKey: [
      HOME_DOMAIN,
      HOME_NOTICE_BANNER_DETAIL_ENDPOINT,
      userState?.userId,
    ],
    queryFn: () => homeApi.getNoticeBanner(),
    staleTime: 30000,
    refetchInterval: 60000,
  });

  const lastTaskUpdatedAt = userState?.lastTaskUpdatedAt; // e.g. 2025-07-12 12:00:00

  useEffect(() => {
    if (taskListState.length > 0 || homeThoughtRecordList.length > 0) {
      const todoTaskList = taskListState.filter((element) => element.isOpen);
      const doneTaskList = taskListState.filter(
        (element) => element.isOpen && element.isDone
      );

      const todoThoughtRecordList = homeThoughtRecordList.filter(
        (element) => element.isOpen
      );
      const doneThoughtRecordList = homeThoughtRecordList.filter(
        (element) => element.isOpen && element.isDone
      );

      const todoCount = todoTaskList.length + todoThoughtRecordList.length;
      const doneCount = doneTaskList.length + doneThoughtRecordList.length;

      if (todoCount > 0) {
        if (doneCount === 0) {
          setBannerMessage("파란 원이 있는 활동을 모두 마쳐주세요.");
        } else if (todoCount === doneCount) {
          const modalContent = getDoneModalContent({
            lastTaskUpdatedAt,
            now: new Date(),
          });

          setBannerMessage(modalContent.title);
        } else if (todoCount > doneCount) {
          setBannerMessage(
            `아직 마치지 않은 활동이 ${todoCount - doneCount}개 있어요.`
          );
        }
      }
    }
  }, [homeThoughtRecordList, lastTaskUpdatedAt, taskListState]);

  useEffect(() => {
    const config = noticeBannerQuery.data?.result;
    if (!config) {
      setShowNoticeBanner(false);
      setBannerMessage(undefined);
      return;
    }

    setShowNoticeBanner(config.isShown);
  }, [noticeBannerQuery.data]);

  const dismissMutation = useMutation({
    mutationFn: () => homeApi.hideNoticeBanner(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          HOME_DOMAIN,
          HOME_NOTICE_BANNER_DETAIL_ENDPOINT,
          userState?.userId,
        ],
      });
    },
    onError: () => {
      setShowNoticeBanner(true);
    },
  });

  const handleHideNoticeBanner = () => {
    setShowNoticeBanner(false);
    dismissMutation.mutate();
  };

  if (!showNoticeBanner || !bannerMessage) return null;

  // Animate only when the message actually changes (not on initial mount)
  const shouldAnimate =
    prevMessageRef.current !== undefined && bannerMessage !== prevMessageRef.current;

  return (
    <Box
      sx={{
        width: "100%",
        height: "42px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: "24px",
        background: isInappWebview
          ? `linear-gradient(174deg, var(--Mono-Colors-Mono-300, #E6EFF2) 6.38%, var(--Mono-Colors-Mono-300, #E6EFF2) 96.37%)`
          : theme.vars.palette.Surface.Backward,
      }}
    >
      <Typography
        key={bannerMessage ?? "__default__"}
        variant={"Caption"}
        sx={{
          color: theme.vars.palette.Content.Highlight,
          fontWeight: 500,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          willChange: shouldAnimate ? "opacity, transform" : undefined,
          animation: shouldAnimate ? "fadeSlideIn 2000ms ease" : "none",
          "@keyframes fadeSlideIn": {
            "0%": { opacity: 0, transform: "translateY(2px)" },
            "100%": { opacity: 1, transform: "translateY(0)" },
          },
        }}
      >
        {bannerMessage}
      </Typography>
      <Box onClick={handleHideNoticeBanner} sx={{ cursor: "pointer" }}>
        <XMarkIconSvg color={theme.vars.palette.Content.Highlight} />
      </Box>
    </Box>
  );
}
